import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ChevronLeft, Send, Ban, PlayCircle } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { fetchBroadcast, broadcastAction, type AdminBroadcast } from '@/lib/adapters'
import { PERMISSIONS } from '@/lib/permissions'
import { InfoRow, RelativeTime } from '@/components/DataDisplay'
import { StatusBadge, type StatusKind } from '@/components/StatusBadge'
import { ErrorState, PermissionDenied, Skeleton } from '@/components/States'
import { useAuth } from '@/providers/AuthProvider'
import { useToast } from '@/providers/UiProviders'

const STATUS_BADGE: Record<string, StatusKind> = {
  draft: 'pending',
  queued: 'review',
  sent: 'active',
  failed: 'rejected',
  cancelled: 'cancelled',
}

/** A15.13 — broadcast karta / holat. Test-yuborish, bekor, navbatga qo'yish. */
export function BroadcastCard() {
  const t = useT()
  const { can } = useAuth()
  const { toast } = useToast()
  const { id = '' } = useParams()
  const bid = Number(id)
  const [b, setB] = useState<AdminBroadcast | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [reload, setReload] = useState(0)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    const ac = new AbortController()
    let alive = true
    setLoading(true)
    setError(null)
    fetchBroadcast(bid, ac.signal)
      .then((r) => {
        if (alive) setB(r)
      })
      .catch((e: unknown) => {
        if (!alive || (e instanceof DOMException && e.name === 'AbortError')) return
        setError(e instanceof Error ? e : new Error(String(e)))
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
      ac.abort()
    }
  }, [bid, reload])

  const run = async (action: 'queue' | 'cancel' | 'test') => {
    if (busy) return
    setBusy(true)
    try {
      await broadcastAction(bid, action)
      toast(t.broadcast.actionOk, { tone: 'success' })
      setReload((n) => n + 1)
    } catch (e) {
      toast(t.broadcast.actionFailed, {
        tone: 'error',
        ...(e instanceof Error && e.message ? { description: e.message } : {}),
      })
    } finally {
      setBusy(false)
    }
  }

  if (!can(PERMISSIONS.USERS_VIEW)) return <PermissionDenied permission={PERMISSIONS.USERS_VIEW} />

  const canQueue = b?.status === 'draft'
  const canCancel = b?.status === 'draft' || b?.status === 'queued'

  return (
    <>
      <PageHeader
        title={b ? b.title || `#${b.id}` : t.broadcast.card}
        screenId="A15.13"
        actions={
          <Link
            to="/broadcasts"
            className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
          >
            <ChevronLeft size={13} />
            {t.broadcast.title}
          </Link>
        }
      />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        {loading ? (
          <div className="max-w-xl space-y-2 rounded-lg border border-border-default bg-surface-2 p-4">
            {Array.from({ length: 5 }, (_, i) => (
              <Skeleton key={i} className="h-4 w-2/3" />
            ))}
          </div>
        ) : error ? (
          <ErrorState code={error.message} onRetry={() => setReload((n) => n + 1)} />
        ) : !b ? (
          <ErrorState title={t.states.notFoundTitle} />
        ) : (
          <div className="max-w-xl space-y-3">
            <div className="rounded-lg border border-border-default bg-surface-2 p-4">
              <div className="mb-3">
                <StatusBadge status={STATUS_BADGE[b.status] ?? 'pending'} />
              </div>
              <InfoRow label={t.broadcast.titleField}>{b.title || '—'}</InfoRow>
              <InfoRow label={t.broadcast.bodyField}>
                <span className="whitespace-pre-wrap">{b.body}</span>
              </InfoRow>
              <InfoRow label={t.broadcast.audience}>{t.broadcast[b.audience as 'all'] ?? b.audience}</InfoRow>
              <InfoRow label={t.broadcast.recipients}>{b.recipients}</InfoRow>
              <InfoRow label={t.col.time}>
                <RelativeTime value={b.created_at} />
              </InfoRow>
              {b.scheduled_at ? (
                <InfoRow label={t.broadcast.schedule}>
                  <RelativeTime value={b.scheduled_at} />
                </InfoRow>
              ) : null}
              {b.test_sent_at ? (
                <InfoRow label={t.broadcast.test}>
                  <RelativeTime value={b.test_sent_at} />
                </InfoRow>
              ) : null}
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              <button
                disabled={busy}
                onClick={() => run('test')}
                className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-3 py-1.5 text-sm text-secondary hover:text-primary disabled:opacity-50"
              >
                <Send size={13} />
                {t.broadcast.test}
              </button>
              {canQueue && (
                <button
                  disabled={busy}
                  onClick={() => run('queue')}
                  className="inline-flex items-center gap-1.5 rounded-md border border-accent bg-accent-subtle px-3 py-1.5 text-sm text-accent hover:bg-accent hover:text-white disabled:opacity-50"
                >
                  <PlayCircle size={13} />
                  {t.broadcast.queue}
                </button>
              )}
              {canCancel && (
                <button
                  disabled={busy}
                  onClick={() => run('cancel')}
                  className="inline-flex items-center gap-1.5 rounded-md border border-danger bg-danger-subtle px-3 py-1.5 text-sm text-danger hover:bg-danger hover:text-white disabled:opacity-50"
                >
                  <Ban size={13} />
                  {t.broadcast.cancel}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  )
}
